import { PlusOutlined } from "@ant-design/icons";
import { Button, Col, Row } from "antd";
import {
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";
import { FormattedMessage } from "react-intl";
import {
  SelectField,
  type SelectFieldProps,
  type SelectOption,
} from "../../form/SelectField";

export interface TableSelectProps<T> extends Omit<SelectFieldProps, "prefix"> {
  entityById?(
    id: string,
    option: any
  ): T | Array<T> | Promise<T[]> | null | undefined;
  onAdd?(selected: Array<T | T[]>): void;
  onSelectedChanged?(selected: Array<T | T[]>): void;
  beforeAdd?(selected: Array<T | T[]>): void;
  afterAdd?(selected: Array<T | T[]>): void;
  render?(props: {
    selectedItems: SelectOption[] | undefined;
    onChange: (value: SelectOption | SelectOption[], options: any) => void;
  }): ReactNode;
  loading?: boolean;
  prefix?(props: {
    setSelectedItems: Dispatch<SetStateAction<SelectOption[] | undefined>>;
  }): ReactNode;
}

export function TableSelect<T>({
  entityById,
  onAdd,
  onSelectedChanged,
  beforeAdd,
  afterAdd,
  render,
  prefix,
  ...selectFieldProps
}: TableSelectProps<T>) {
  const [selectedItems, setSelectedItems] = useState<SelectOption[]>();
  const [selectedEntities, setSelectedEntities] = useState<
    Record<string, T | T[]>
  >({});
  const [loadingItems, setLoadingItems] = useState(false);

  const onSelectChange = async (
    value: SelectOption | SelectOption[],
    options: any
  ) => {
    setLoadingItems(true);

    const arrVal = Array.isArray(value) ? value : [value];

    setSelectedItems(arrVal);

    if (!value) {
      setSelectedEntities({});
      return;
    }

    const entities = {} as Record<string, T | T[]>;

    for (const val of arrVal) {
      // antd v5's `labelInValue` returned {key, label, value} — `key` was an
      // alias for `value`. v6 removed that alias and returns only {label, value}.
      // Our SelectField builds options with the option's identifier on `.key`
      // (and renders `<Select.Option key={o.key} value={o.key}>`), so the v6
      // `val.value` is what matches `option.key`.
      const lookupKey = (val as any).value ?? (val as any).key;
      const option = options.find((o: any) => o.key == lookupKey);
      const key = String(lookupKey);

      const entity =
        selectedEntities[key] ||
        (entityById && (await entityById(key, option || {})));

      if (entity) {
        entities[key] = entity;
      }
    }

    setSelectedEntities(entities);
    if (onSelectedChanged) onSelectedChanged(Object.values(entities));

    setLoadingItems(false);
  };

  return (
    <Row gutter={[16, 8]} wrap style={{ flex: 1 }}>
      {prefix && (
        <Col xs={24} sm={0}>
          {prefix({ setSelectedItems })}
        </Col>
      )}

      <Col flex="auto" style={{ display: "flex" }}>
        {prefix && (
          <div className="hide-xs" style={{ marginRight: "16px" }}>
            {prefix({ setSelectedItems })}
          </div>
        )}

        {render ? (
          render({ selectedItems, onChange: onSelectChange })
        ) : (
          <SelectField
            labelInValue
            value={selectedItems}
            style={{ width: "100%" }}
            onChange={onSelectChange}
            {...selectFieldProps}
          />
        )}
      </Col>

      <Col>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          block
          loading={loadingItems}
          onClick={() => {
            const selected = Object.values(selectedEntities);
            beforeAdd && beforeAdd(selected);
            onAdd && onAdd(selected);
            afterAdd && afterAdd(selected);

            setSelectedEntities({});
            setSelectedItems([]);
          }}
        >
          <span className="hide-xs">
            <FormattedMessage id="add" defaultMessage="add" />
          </span>
        </Button>
      </Col>
    </Row>
  );
}
